import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {IVideo} from '@api/models/video/video';
import {
  AdaptationPlatformConfig,
  AdBreakConfig,
  AdTagType,
  AdvertisingConfig,
  DownloadedVideoData,
  Player,
  PlayerAPI,
  PlayerConfig,
  PlayerEvent,
  SourceConfig,
  ViewMode,
} from 'bitmovin-player';
import {SharedConsentService} from '@utils/services/shared-consent/shared-consent.service';
import {isPlatformBrowser} from '@angular/common';
import {AnalyticsHttpService} from '@api/http/analytics/analytics-http.service';
import {AnalyticsViewType} from '@api/models/analytics/analytics-view-type';
import {ImageService} from '@utils/services/image/image.service';
import {IMAGE_RESOLUTION} from '@api/models/image/image-resolution';
import {AdCampaignService} from '@components/ad-campaign/services/ad-campaign.service';
import {AnalyticsService} from '@utils/services/analytics/analytics.service';
import {ManualScreenName} from '@utils/services/analytics/model/screen-name';

const BitmovinLicenseKey = '09f4647b-29ef-4dc1-8cad-2a04b28cbfe9';
// const AnalyticsLicenseKey = '3A982407-FC66-41B5-90F2-31E33B43821A';

export interface IPlayerSettings {
  video: IVideo;
  hlsUrl: string;
  setupOnInit?: boolean;
  autostart?: boolean;
  showAd?: boolean;
  canPlay?: boolean;
}

@Component({
  selector: 'lib-bitmovin-player',
  templateUrl: './bitmovin-player.component.html',
  styleUrls: ['./bitmovin-player.component.scss'],
})
export class BitmovinPlayerComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public playerApi: PlayerAPI;
  public playedByUI = false;
  public pausedByUI = false;
  public mutedByUI = false;
  public playedOnce = false;
  public loading = false;
  public videoReady = false;
  public posterImage: string;
  public adRunning = false;
  public showAdControls = false;
  public hasError = false;
  public showControls = false;
  public isFullscreen = false;
  public playerClick = new EventEmitter<MouseEvent>();
  public ready = new EventEmitter<DownloadedVideoData>();
  public settings: IPlayerSettings;
  @ViewChild('bmPlayerComponent') private bmPlayerElement: ElementRef;
  private tcfConsentString: string;
  private toggleIssuer: string;
  private controlBarObserver: MutationObserver;
  private controlBarElement: Element | null = null;
  private lastVolume: number;

  constructor(
    private analyticsHttpService: AnalyticsHttpService,
    private adCampaignService: AdCampaignService,
    private analyticsService: AnalyticsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  public ngOnInit(): void {
    this.setPosterImage();
  }

  public async ngAfterViewInit(): Promise<void> {
    if (this.settings.setupOnInit) {
      await this.setup();
    }
  }

  public ngOnDestroy(): void {
    this.disconnectControlBarObserver();
    this.playerApi?.destroy();
  }

  private setupControlBarObserver(): void {
    // Wait for the player to be fully initialized
    setTimeout(() => {
      this.findControlBarElement();
    }, 1000);
  }

  private findControlBarElement(): void {
    // Find the control bar element
    this.controlBarElement = document.querySelector('.bmpui-ui-controlbar');

    if (this.controlBarElement) {
      console.log('Control bar element found:', this.controlBarElement);
      this.observeControlBarVisibility();
    } else {
      // If not found, try again after a short delay
      setTimeout(() => this.findControlBarElement(), 500);
    }
  }

  private observeControlBarVisibility(): void {
    if (!this.controlBarElement) return;

    // Disconnect any existing observer
    this.disconnectControlBarObserver();

    // Create a new observer
    this.controlBarObserver = new MutationObserver((mutations) => {
      // Check if the control bar is visible
      const isVisible = this.isControlBarVisible();
      this.showControls = isVisible;
    });

    // Start observing the control bar element
    this.controlBarObserver.observe(this.controlBarElement, {
      attributes: true,
      attributeFilter: ['class'],
      childList: false,
      subtree: false,
    });

    // Initial check
    const isVisible = this.isControlBarVisible();
    this.showControls = isVisible;
  }

  private isControlBarVisible(): boolean {
    if (!this.controlBarElement) {
      return false;
    }
    return !this.controlBarElement.classList.contains('bmpui-hidden');
  }

  private disconnectControlBarObserver(): void {
    if (this.controlBarObserver) {
      this.controlBarObserver.disconnect();
      this.controlBarObserver = null;
    }
  }

  public onPlayerClick(event: MouseEvent): void {
    // Prevent default click behaviour completely - needed for carousel
    event.stopPropagation();
    event.preventDefault();
    this.playerClick.emit(event);
  }

  @HostListener('mouseenter')
  public onMouseEnter(): void {
    if (this.adRunning) {
      this.showAdControls = true;
    }
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    this.showAdControls = false;
  }

  public updateCanPlay(canPlay: boolean): void {
    this.settings.canPlay = canPlay;
  }

  public async onViewportChange({
    target,
    visible,
  }: {
    target: Element;
    visible: boolean;
  }): Promise<void> {
    if (visible && !this.hasError) {
      await this.loadVideo();
    }
  }

  public seek(seconds: number): void {
    if (this.playerApi) {
      const newTime = this.playerApi.getCurrentTime() + seconds;
      this.playerApi.isLive()
        ? this.playerApi.timeShift(newTime)
        : this.playerApi.seek(newTime);
    }
  }

  public mute(): void {
    this.playerApi?.mute();
  }

  public unmute(): void {
    this.playerApi?.unmute();
  }

  public toggleMute(): void {
    this.playerApi?.isMuted() ? this.playerApi?.unmute() : this.mute();
  }

  public async setup(): Promise<void> {
    if (this.hasError) {
      return;
    }
    this.tcfConsentString = SharedConsentService.consent.CMP;
    if (this.playerApi?.getSource()) {
      return;
    }
    if (this.loading || this.videoReady) {
      return;
    }
    if (!this.playerApi) {
      await this.init();
    }
  }

  public getVideoSource(): SourceConfig {
    const source: SourceConfig = {};
    if (this.settings.video?.url) {
      source.progressive = this.settings.video.url;
    }
    if (this.settings.video?.bm_player_url) {
      source.hls = this.settings.video.bm_player_url;
    }
    if (this.settings.hlsUrl) {
      source.hls = this.settings.hlsUrl;
    }
    return source;
  }

  public async init(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    let advertising: AdvertisingConfig;
    let adaptation: AdaptationPlatformConfig;
    if (this.settings.showAd) {
      const videoId = this.settings.video.videoid;
      const targeting =
        await this.adCampaignService.getVideoAdTargeting(videoId);
      let adUrl =
        'https://pubads.g.doubleclick.net/gampad/ads' +
        '?iu=/6771346/sfa_fan.at/web.fan.at/news/instream_preroll1' +
        '&description_url=https%3A%2F%2Ffan.at&tfcd=0&npa=0&sz=640x360' +
        '&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&vpos=preroll' +
        '&correlator=' +
        Date.now() +
        '&plcmt=' +
        (this.settings.autostart ? '1' : '2') +
        '&vpmute=1';
      // Append video ad targeting
      if (targeting) {
        const params = encodeURIComponent(`channel=${targeting.value}`);
        adUrl += `&cust_params=${params}`;
      }
      // Append GDPR TCF 2.0 consent string
      if (this.tcfConsentString?.length > 0) {
        adUrl += `&gdpr_consent=${this.tcfConsentString}`;
      }
      const adTag: AdBreakConfig = {
        id: 'preroll-ad',
        tag: {
          url: adUrl,
          type: AdTagType.VAST,
        },
        position: 'pre',
      };
      advertising = {
        adBreaks: [adTag],
      };
    }

    const config: PlayerConfig = {
      key: BitmovinLicenseKey,
      // analytics: {
      //   key: AnalyticsLicenseKey,
      // },
      advertising,
      adaptation,
      buffer: {
        video: {
          forwardduration: 20,
        },
      },
    };

    if (this.bmPlayerElement?.nativeElement) {
      this.playerApi = new Player(this.bmPlayerElement.nativeElement, config);
      console.log('playerApi mute');
      this.playerApi.mute();
      this.registerPlayerEvents();
      console.log('Native player created', this.bmPlayerElement.nativeElement);
    }
  }

  public isPlaying(): boolean {
    return this.playerApi?.isPlaying();
  }

  public async play(): Promise<void> {
    if (!this.settings.canPlay) {
      return;
    }
    if (!this.videoReady) {
      await this.setup();
    }
    await this.playerApi.play();
  }

  public async pause(): Promise<void> {
    if (!this.videoReady) {
      return;
    }
    await this.playerApi.pause();
  }

  public setVolume(volume: number): void {
    this.playerApi.setVolume(volume);
  }

  public togglePlay(): void {
    if (!this.settings.canPlay) {
      return;
    }
    this.toggleIssuer = 'ui';
    this.playerApi?.isPlaying()
      ? this.playerApi?.pause()
      : this.playerApi?.play();
  }

  private async loadVideo(): Promise<void> {
    if (this.loading || this.videoReady) {
      return;
    }

    this.loading = true;

    if (!this.playerApi) {
      await this.init();
    }

    try {
      await this.playerApi?.load(this.getVideoSource());
    } catch (e) {
      this.loading = false;
      await this.playerApi?.destroy();
      this.playerApi = null;
    }
  }

  private setPosterImage(): void {
    this.posterImage =
      ImageService.getPictureUrl(
        this.settings.video?.preview,
        IMAGE_RESOLUTION.HIGH
      ) || this.settings.video?.preview_url;
  }

  private registerPlayerEvents(): void {
    this.playerApi.on(PlayerEvent.AdStarted, () => {
      this.adRunning = true;
    });
    this.playerApi.on(PlayerEvent.AdFinished, () => {
      this.adRunning = false;
    });
    this.playerApi.on(PlayerEvent.AdSkipped, () => {
      this.adRunning = false;
    });
    this.playerApi.on(PlayerEvent.AdError, async (err: any) => {
      console.warn('[BM Player] Ad error occured', err);
      this.adRunning = false;
    });
    this.playerApi.on(PlayerEvent.Muted, (ev: any) => {
      this.mutedByUI = ev.issuer === 'ui-volumecontroller';
      if (ev.issuer === 'ui-volumecontroller') {
        localStorage.setItem('fanat-volume', '0');
      }
    });
    this.playerApi.on(PlayerEvent.VolumeChanged, (ev: any) => {
      if (ev.issuer === 'ui-volumecontroller') {
        localStorage.setItem('fanat-volume', ev.targetVolume.toString());
        this.lastVolume = ev.targetVolume;
      }
    });
    this.playerApi.on(PlayerEvent.Unmuted, (ev: any) => {
      this.mutedByUI = ev.issuer === 'ui-volumecontroller';
      if (ev.issuer === 'ui-volumecontroller') {
        localStorage.setItem('fanat-volume', this.lastVolume.toString());
      }
    });
    this.playerApi.on(PlayerEvent.SourceLoaded, async (err: any) => {
      if (!this.playerApi.isLive()) {
        return;
      }
      this.handleVideoReady();
    });
    this.playerApi.on(PlayerEvent.Ready, this.handleVideoReady.bind(this));
    this.playerApi.on(PlayerEvent.Play, (ev: any) => {
      this.analyticsService.sendPageView({ manualScreenName: ManualScreenName.VIDEO_PLAY })
      if (!this.settings.canPlay) {
        this.playerApi.pause();
        return;
      }
      if (!this.playedOnce) {
        this.playedOnce = true;
        this.analyticsService.sendPageView();
        if (this.settings.video?.videoid) {
          this.analyticsHttpService.patchAddView(
            this.settings.video.videoid,
            AnalyticsViewType.Video
          );
        }
      } else {
        // Unmute player on user play event
        this.playerApi?.unmute();
      }
      this.playedByUI = ev.issuer === 'ui' || this.toggleIssuer === 'ui';
      this.toggleIssuer = null;
    });
    this.playerApi.on(PlayerEvent.Paused, (ev: any) => {
      this.pausedByUI = ev.issuer === 'ui' || this.toggleIssuer === 'ui';
      this.toggleIssuer = null;
    });
    this.playerApi.on(PlayerEvent.Error, async (err) => {
      console.error('[BM Player] Player error occured', err);
      this.hasError = true;
    });
    this.playerApi.on(PlayerEvent.ViewModeChanged, async (event: any) => {
      this.isFullscreen = event.to === ViewMode.Fullscreen;
    });

    // Setup the control bar observer after player is initialized
    this.setupControlBarObserver();
  }

  private handleVideoReady(): void {
    if (this.videoReady) {
      return;
    }
    this.videoReady = true;
    this.loading = false;
    this.ready.emit(this.playerApi.getDownloadedVideoData());
  }
}
